import {FormComponentProps} from "antd/lib/form";
import {Col, DatePicker, Form, Row, Select, Tooltip} from "antd";
import './ticket-form.style.less';
import React, {HTMLAttributes} from "react";
import {ComplianceTicketFormData, VeriflytTicketFormFields} from "../types";
import {createFormFields} from "common/form";
import VeriflytTicketTemplateSingleSelect from "../../../form/select/veriflyt-ticket-template-select/single-select";
import {useTicketTemplate} from "../graphql";
import moment from "moment";
import {getCompletionDateValidator, getExpirationDateValidator} from "../validation";
import {GetFieldDecoratorOptions} from "antd/lib/form/Form";

export interface VeriflytTicketProps {
    data: ComplianceTicketFormData,
    onChange?: (newFormData: ComplianceTicketFormData) => void,
    wrapperProps?: HTMLAttributes<HTMLDivElement>,
    disabledTicketTemplateIds?: string[],
    disabledTicketTemplateMessage?: string,
    readOnly?: boolean
}

type PropsWithForm = VeriflytTicketProps & FormComponentProps<ComplianceTicketFormData>

const VeriflytTicketForm_Internal: React.FC<PropsWithForm> =
    ({
        form,
        wrapperProps,
        disabledTicketTemplateIds,
        disabledTicketTemplateMessage,
        readOnly
    }) => {

    const fields: React.ReactNode[] = [];

    const isNew = !form.getFieldValue('_id');

    const ticketTemplateID = form.getFieldValue(VeriflytTicketFormFields.ticketTemplateIDAndName)?.key;

    const ticketTemplateResult = useTicketTemplate(ticketTemplateID);

    const createAgainMessage = 'To change, delete this record and create a new one.';

    fields.push(
        <Col xs={24} sm={12} lg={6}>
            <Tooltip title={isNew ? '' : 'You cannot change the certification of an existing record. ' + createAgainMessage}>
                <Form.Item label="Certification" className="ant-form-item-no-margin">
                    {form.getFieldDecorator(VeriflytTicketFormFields.ticketTemplateIDAndName, {
                        rules: [{ required: true, message: 'Please select a certification' }]
                    })(
                        <VeriflytTicketTemplateSingleSelect
                            disabledMessage={disabledTicketTemplateMessage}
                            disabledTicketTemplateIds={disabledTicketTemplateIds}
                            disabled={Boolean(readOnly) || !isNew}
                        />
                    )}
                </Form.Item>
            </Tooltip>
        </Col>
    )

    const completionDateRules: GetFieldDecoratorOptions['rules'] = [
        {
            required: true, message: 'Please choose a completion date'
        }
    ];

    if (isNew){
        completionDateRules.push({
            validator: getCompletionDateValidator(form)
        });
    }

    const expirationDateRules: GetFieldDecoratorOptions['rules'] = [
        {
            required: true, message: 'Please choose an expiration date'
        },
        {
            validator: getExpirationDateValidator(form)
        }
    ];

    fields.push(...[
        <Col xs={24} sm={6} lg={6}>
            <Tooltip title={isNew ? '' : 'You cannot change the completion date of an existing record.' + createAgainMessage}>
                <Form.Item label="Completion Date" className="ant-form-item-no-margin">
                    {form.getFieldDecorator(VeriflytTicketFormFields.completionDate, {
                        rules: completionDateRules
                    })(
                            <DatePicker
                                style={{ width: '100%' }}
                                disabled={Boolean(readOnly) || !isNew}
                                disabledDate={(current) => current.isAfter(moment(), 'day')}
                            />
                    )}
                </Form.Item>
            </Tooltip>
        </Col>,
        <Col xs={24} sm={6} lg={6}>
            <Form.Item label="Expiration Date" className="ant-form-item-no-margin">
                {form.getFieldDecorator(VeriflytTicketFormFields.expirationDate, {
                    rules: expirationDateRules
                })(
                    <DatePicker
                        style={{ width: '100%' }}
                        disabled={Boolean(readOnly)}
                        disabledDate={(current) => (
                            current.isSameOrBefore(form.getFieldValue(VeriflytTicketFormFields.completionDate), 'day')
                        )}
                    />
                )}
            </Form.Item>
        </Col>
    ])

    if (ticketTemplateID && ticketTemplateResult?.ticketTemplate){
        // TODO: Based on the requirements of the ticket template, add more fields such as grade, score, etc.

        fields.push(
            <Col xs={24} sm={6} lg={6}>
                <Form.Item label="Passed" className="ant-form-item-no-margin">
                    {form.getFieldDecorator(VeriflytTicketFormFields.result_passed, {
                        rules: [{
                            required: true, message: 'Please whether this person passed or not'
                        }]
                    })(
                        <Select style={{ width: '100%' }} disabled={Boolean(readOnly)}>
                            <Select.Option key="YES" value={1}>YES</Select.Option>
                            <Select.Option key="NO" value={0}>NO</Select.Option>
                        </Select>
                    )}
                </Form.Item>
            </Col>
        )
    }

    const content = (
        <Row gutter={6}>
            {fields}
        </Row>
    )

    return <div
        className="compliance-ticket-form"
        {...wrapperProps}
    >{content}</div>
}

const TicketForm = Form.create<PropsWithForm>({
    mapPropsToFields({ data }){
        return createFormFields(data);
    },
    onFieldsChange(props, changedFields){
        if (typeof props.onChange === 'function'){
            props.onChange({
                ...props.data,
                ...changedFields
            });
        }
    }
})(VeriflytTicketForm_Internal as any)

TicketForm.displayName = 'VeriflytTicketForm'

export default TicketForm